import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { post, get } from "../../services/smartApiService";
import { INDEX_COMPANY_URLS, COMPANY_URLS } from "../../api/ManageUrls";
import { showAlertAutoClose } from "../../services/notifyService";
import { SdsForm } from "soft_digi";
import { SdsFormElementProps } from "soft_digi/dist/sdforms/SdsFromInterFace";

/* -------------------- PROPS -------------------- */

interface IndexCompanyFormProps {
  loadTableData: () => void;
  selectedIndexId?: number;
}

/* -------------------- VALIDATION -------------------- */

const schema = Yup.object({
  index_id: Yup.object().required("Index is required"),
  company_id: Yup.object().required("Company is required"),
  weightage: Yup.number()
    .min(0, "Weightage must be at least 0")
    .max(100, "Weightage cannot exceed 100")
    .nullable(),
});

/* -------------------- COMPONENT -------------------- */

const IndexCompanyForm: React.FC<IndexCompanyFormProps> = ({
  loadTableData,
  selectedIndexId,
}) => {
  const [loading, setLoading] = useState(false);
  const [indices, setIndices] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<any>({
    resolver: yupResolver(schema),
    defaultValues: {
      index_id: selectedIndexId ?? null,
      company_id: null,
      weightage: null,
    },
  });

  /* -------------------- LOAD DROPDOWN DATA -------------------- */

  useEffect(() => {
    const indexSub = get(INDEX_COMPANY_URLS.GET_ALL_SELECT).subscribe({
      next: (res: any) => {
        setIndices(res.data || []);
      },
    });

    const companySub = get(INDEX_COMPANY_URLS.GET_ALL_SELECT).subscribe({
      next: (res: any) => {
        setCompanies(res.data || []);
      },
    });

    return () => {
      indexSub.unsubscribe();
      companySub.unsubscribe();
    };
  }, []);

  /* -------------------- FILTER OPTIONS -------------------- */

  const indexOptions = indices
    .filter((i) => i.entity_type === "INDEX")
    .map((i) => ({
      label: i.label,
      value: i.value,
    }));

  const companyOptions = companies
    .filter((c) => c.entity_type === "COMPANY")
    .map((c) => ({
      label: c.label,
      value: c.value,
    }));

  /* -------------------- SUBMIT -------------------- */

  const onSubmit = (data: any) => {
    setLoading(true);

    const payload = {
      index_id: data?.index_id?.value,
      company_id: data?.company_id?.value,
      weightage: data?.weightage,
    };

    const subscription = post(INDEX_COMPANY_URLS.INSERT, payload).subscribe(
      () => {
        showAlertAutoClose("Added Successfully", "success");

        reset({
          index_id: selectedIndexId ?? null,
          company_id: null,
          weightage: null,
        });

        loadTableData();
        setLoading(false);
      },
      () => {
        showAlertAutoClose("Failed to add company to index", "error");
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  };

  /* -------------------- FORM ELEMENTS -------------------- */

  const indexCompanyElements: SdsFormElementProps[] = [
    {
      type: "SELECT_BOX",
      width: 4,
      name: "index_id",
      element: {
        label: "Index",
        isRequired: true,
        options: indexOptions,
        disabled: !!selectedIndexId,
        inputType: "BORDER_LABEL",
      },
    },
    {
      type: "SELECT_BOX",
      width: 4,
      name: "company_id",
      element: {
        label: "Company",
        isRequired: true,
        options: companyOptions,
        inputType: "BORDER_LABEL",
      },
    },
    {
      type: "TEXT_BOX",
      width: 4,
      name: "weightage",
      element: {
        label: "Weightage (%)",
        placeHolder: "Enter weightage",
        inputType: "BORDER_LABEL",
      },
    },
    {
      type: "BUTTON",
      width: 12,
      name: "submit",
      element: {
        label: loading ? "Adding..." : "Add to Index",
        classList: ["button", "is-primary"],
        disabled: loading,
        onClick: handleSubmit(onSubmit),
      },
    },
  ];

  /* -------------------- RENDER -------------------- */

  return (
    <div className="box">
      <SdsForm
        elements={indexCompanyElements}
        control={control}
        errors={errors}
      />
    </div>
  );
};

export default IndexCompanyForm;
